import { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { FiberManualRecord as BulletIcon } from '@mui/icons-material';

interface NewPasswordRequiredProps {
  onConfirmPassword: (newPassword: string, userAttributes?: Record<string, string>) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
  username: string;
}

export function NewPasswordRequired({ 
  onConfirmPassword, 
  isLoading, 
  error, 
  onClearError,
  username 
}: NewPasswordRequiredProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    onClearError();

    if (!newPassword || !confirmPassword) {
      setValidationError('Please enter and confirm your new password');
      return;
    }

    if (!firstName.trim()) {
      setValidationError('Please enter your first name');
      return;
    }

    if (!lastName.trim()) {
      setValidationError('Please enter your last name');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    const userAttributes = {
      given_name: firstName.trim(),
      family_name: lastName.trim(),
    };

    console.log('Form submission - userAttributes:', userAttributes);
    console.log('Form submission - newPassword length:', newPassword.length);
    
    await onConfirmPassword(newPassword, userAttributes);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 400,
        mx: 'auto',
        p: 4,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography variant="h5" component="h2" align="center" mb={2}>
        Password Change Required
      </Typography>
      
      <Typography variant="body1" color="text.secondary" align="center" mb={2}>
        Your account requires a new password. Please set a new password to continue.
      </Typography>

      <Typography variant="body2" mb={2}>
        <strong>Username:</strong> {username}
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          id="firstName"
          label="First Name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={isLoading}
          placeholder="Enter your first name"
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          id="lastName"
          label="Last Name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={isLoading}
          placeholder="Enter your last name"
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          id="newPassword"
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
          placeholder="Enter new password"
          margin="normal"
          variant="outlined"
        />

        <TextField
          fullWidth
          id="confirmPassword"
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          placeholder="Confirm new password"
          margin="normal"
          variant="outlined"
          sx={{ mb: 2 }}
        />

        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || validationError}
          </Alert>
        )}

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Password requirements:
          </Typography>
          <List dense sx={{ pt: 0 }}>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <BulletIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText 
                primary="At least 8 characters long" 
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <BulletIcon sx={{ fontSize: 8 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Should include a mix of uppercase, lowercase, numbers, and symbols" 
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
          </List>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            position: 'relative',
            height: 48,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
              Updating Password...
            </>
          ) : (
            'Set New Password'
          )}
        </Button>
      </Box>
    </Paper>
  );
}